import {tokens} from '@commercial-helios/ui/theme'
import type {ReactNode} from 'react'
import React from 'react'
import styled from 'styled-components'

import Header from '@/components/header'
import {useSteps} from '@/components/step-wizard'
import Button from '@helios/shared/components/button'
import Image from '@helios/shared/components/image'
import Input from '@helios/shared/components/input'
import LocaleSwitcher from '@helios/shared/components/locale-switcher'
import {Section, Layout} from '@helios/shared/styles/grid'
import {Text} from '@helios/shared/styles/typography'
import mq from '@helios/shared/utils/media-query'

import type {State, Data, SetValue} from '../expense-policy-builder'

interface Props {
    state: State['welcome']
    setValue: SetValue<'welcome'>
    data: Data['welcome'][number]
    children: ReactNode
}

const Welcome = ({state, setValue, data}: Props) => {
    const {navigate} = useSteps()
    const [disabled, setDisabled] = React.useState(true)

    // This is needed to update the button state during SSR
    React.useEffect(() => {
        setDisabled(!state.companyName)
    }, [state])

    const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault()

        navigate.next()
    }

    return (
        <Wrapper>
            <Header />

            <StyledSection padding="small">
                <Layout variant="two-asymmetrical">
                    <Form onSubmit={handleSubmit}>
                        <Text as="h1" variant="heading-base">
                            {data.title}
                        </Text>
                        <Text variant="paragraph-large">{data.body}</Text>
                        <Input
                            label={data.company_name}
                            placeholder={data.company_name_placeholder}
                            value={state.companyName}
                            onChange={(value) => setValue('companyName', value)}
                            autoFocus
                        />

                        <Button type="submit" disabled={disabled}>
                            {data.start_button_label}
                        </Button>
                        <LocaleSwitcher />
                    </Form>
                    <StyledImage image={data.image} />
                </Layout>
            </StyledSection>
        </Wrapper>
    )
}

const Wrapper = styled.div`
    display: grid;
    grid-template-rows: auto 1fr;

    ${mq.max('sm')} {
        min-height: 100vh;
    }
`

const StyledSection = styled(Section)`
    display: grid;
    grid-template-rows: 1fr;

    ${mq.max('sm')} {
        min-height: 100%;
        padding-bottom: ${tokens.spacing24};
    }
`

const StyledImage = styled(Image)`
    ${mq.max('sm')} {
        display: none;
    }
`

const Form = styled.form`
    display: grid;
    row-gap: ${tokens.spacing20};
    grid-template-rows: auto auto auto 1fr;
    height: 100%;
    justify-items: left;
    align-items: start;
`

export default Welcome
